import {Injectable} from '@nestjs/common';
import {NodeFileSystem, IFileSystem} from '../../infrastructure/filesystem/node-file-system';

/**
 * MCP 工具定义结构
 */
export interface MCPToolDefinition {
  name: string;
  description: string;
  parameters: {
    type: string;
    properties: Record<string, {type: string; description: string}>;
    required?: string[];
  };
}

/**
 * MCP 服务核心
 * 集中管理文件系统读写、目录扫描等工具，并具备防越权安全校验
 */
@Injectable()
export class McpService {
  private fileSystem: IFileSystem = new NodeFileSystem();

  /**
   * 获取当前工作区根目录
   */
  private getWorkspaceRoot(): string {
    return process.cwd();
  }

  /**
   * 纯 JS 跨平台路径解析与防越权校验
   * 防止通过 ../ 访问工作区根目录之外的系统敏感文件
   */
  private resolvePath(targetPath: string, root: string): string {
    const cleanRoot = root.replace(/\\/g, '/').replace(/\/+$/, '');
    const cleanTarget = (targetPath || '').replace(/\\/g, '/');
    const baseSegments = cleanRoot.split('/').filter(Boolean);
    const targetSegments = cleanTarget.split('/').filter(Boolean);

    const resolvedSegments = [...baseSegments];
    for (const segment of targetSegments) {
      if (segment === '..') {
        resolvedSegments.pop();
      } else if (segment !== '.' && segment !== '') {
        resolvedSegments.push(segment);
      }
    }

    const isWinDrive = /^[a-zA-Z]:/.test(cleanRoot);
    const resolved = (isWinDrive ? '' : '/') + resolvedSegments.join('/');
    const normalizedRoot = (isWinDrive ? '' : '/') + baseSegments.join('/');

    if (!resolved.startsWith(normalizedRoot)) {
      throw new Error('安全错误：禁止访问工作区根目录之外的路径。');
    }
    return resolved;
  }

  /**
   * 获取所有可用的文件操作工具定义
   */
  public getToolDefinitions(): MCPToolDefinition[] {
    return [
      {
        name: 'list_directory',
        description: '列出指定目录下的文件和文件夹',
        parameters: {
          type: 'object',
          properties: {
            path: {type: 'string', description: '相对工作区路径，空字符串表示根目录'},
          },
        },
      },
      {
        name: 'read_file',
        description: '读取指定文件的文本内容',
        parameters: {
          type: 'object',
          properties: {
            path: {type: 'string', description: '文件相对路径'},
          },
          required: ['path'],
        },
      },
      {
        name: 'write_file',
        description: '向指定文件写入或创建内容',
        parameters: {
          type: 'object',
          properties: {
            path: {type: 'string', description: '文件相对路径'},
            content: {type: 'string', description: '要写入的完整文本内容'},
          },
          required: ['path', 'content'],
        },
      },
    ];
  }

  /**
   * 执行指定的 MCP 工具
   * @param name 工具名称
   * @param args 工具参数
   */
  public async executeTool(name: string, args: any): Promise<string> {
    const root = this.getWorkspaceRoot();
    try {
      switch (name) {
        case 'list_directory': {
          const targetPath = this.resolvePath(args?.path || '', root);
          const entries = await this.fileSystem.readDirectory(targetPath);
          return JSON.stringify(entries, null, 2);
        }
        case 'read_file': {
          if (!args?.path) return '错误：read_file 缺少 path 参数';
          const filePath = this.resolvePath(args.path, root);
          const stats = await this.fileSystem.stat(filePath);
          if (stats.isDirectory()) {
            return `错误："${args.path}" 是一个目录而不是文件。`;
          }
          return await this.fileSystem.readFile(filePath);
        }
        case 'write_file': {
          if (!args?.path) return '错误：write_file 缺少 path 参数';
          const filePath = this.resolvePath(args.path, root);
          await this.fileSystem.writeFile(filePath, args.content || '');
          return `成功：文件 ${args.path} 已写入。`;
        }
        default:
          return `未知工具: ${name}`;
      }
    } catch (err: any) {
      return `执行工具 ${name} 失败: ${err.message}`;
    }
  }
}
